import React, { useEffect, useState } from 'react';
import EventTraceabilityStatus from '../../components/traceability/EventTraceabilityStatus';
import { displayStatus } from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, evaluationsAPI, eventsAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { ClipboardList, ArrowRight, AlertTriangle, MessageSquare, Calendar } from 'lucide-react';
import { EVENT_STATUS, RISK_LEVEL } from '../../lib/eventConstants';

export default function LevantadorDashboard() {
  const { activeUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([projectsAPI.list(), evaluationsAPI.list(), eventsAPI.list().catch(() => ({ data: { items: [] } }))])
      .then(([p, e, ev]) => {
        setProjects(p.data.items || []);
        setEvaluations(e.data.items || []);
        // Only events where levantador is assigned
        const myEvents = (ev.data.items || []).filter(evt => evt.assigned_levantador_id === activeUser?.id);
        setEvents(myEvents);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeUser?.id]);

  const drafts = evaluations.filter((e) => e.status === 'borrador');
  const returned = evaluations.filter((e) => e.status === 'regresada');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="levantador-dashboard" id="dashboard-levantador">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Dashboard — Levantador</h1>
        <p className="text-slate-500 text-sm mt-1">Bienvenido, {activeUser?.name}</p>
      </div>

      {/* Prominent empty state when levantador has no projects at all */}
      {projects.length === 0 && evaluations.length === 0 && events.length === 0 && (
        <div className="soem-card p-10 text-center" data-testid="empty-levantador-state">
          <ClipboardList size={48} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-soem-navy mb-2">Sin proyectos asignados</h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Contacta a tu Líder de Proyecto para que te asigne a un proyecto.
            Una vez asignado, aquí verás tus evaluaciones y checklists pendientes.
          </p>
        </div>
      )}

      {/* Returned evaluations alert */}
      {returned.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 flex items-center gap-3" data-testid="returned-alert">
          <MessageSquare size={20} className="text-orange-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-orange-800">{returned.length} evaluación(es) regresadas por el líder</p>
            <p className="text-orange-600 text-sm">Revisa los comentarios y realiza las correcciones necesarias.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { label: 'Proyectos Asignados', value: projects.length, color: '#1A56DB' },
          { label: 'Evaluaciones en Borrador', value: drafts.length, color: '#D97706' },
          { label: 'Regresadas con Comentarios', value: returned.length, color: '#DC2626' },
        ].map((s) => (
          <div key={s.label} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
            <p className="soem-label">{s.label}</p>
            <p className="text-3xl font-black text-soem-navy mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Returned evaluations */}
        {returned.length > 0 && (
          <div className="soem-card border-t-4 border-t-orange-500" data-testid="returned-evals-card">
            <div className="p-5 border-b border-slate-100 flex items-center gap-2">
              <MessageSquare size={16} className="text-orange-600" />
              <h2 className="font-bold text-soem-navy">Regresadas — Atención Requerida</h2>
            </div>
            <div className="divide-y divide-slate-50">
              {returned.map((e) => (
                <div
                  key={e.id}
                  className="flex items-start gap-3 px-5 py-4 hover:bg-orange-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/evaluaciones/${e.id}/checklist`)}
                  data-testid={`returned-eval-${e.id}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-soem-navy text-sm">
                      {e.general_fields?.instalacion || 'Sin nombre'} v{e.version}
                    </p>
                    {e.return_comments && (
                      <div className="mt-2 bg-orange-100 border border-orange-200 rounded p-2 text-xs text-orange-800">
                        <strong>Comentario del líder:</strong> {e.return_comments}
                      </div>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-slate-300 mt-1 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Draft evaluations */}
        <div className="soem-card" id="drafts-card" data-testid="drafts-card">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <ClipboardList size={16} className="text-soem-primary" />
            <h2 className="font-bold text-soem-navy">Borradores</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {drafts.length === 0 ? (
              <p className="text-slate-400 text-sm p-5">No hay evaluaciones en borrador.</p>
            ) : (
              drafts.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between px-5 py-4 hover:bg-blue-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/evaluaciones/${e.id}/checklist`)}
                  data-testid={`draft-eval-${e.id}`}
                >
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">
                      {e.general_fields?.instalacion || 'Sin nombre'} v{e.version}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge status={displayStatus(e)} />
                      <ScoreSummary evaluation={e} />
                      <SemaforoBadge semaforo={e.semaforo} size="sm" />
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-slate-300" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* My projects */}
        <div className="soem-card" id="mis-proyectos-lev-card" data-testid="my-projects-lev-card">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-soem-navy">Mis Proyectos</h2>
            <button onClick={() => navigate('/dashboard/proyectos')} className="text-soem-primary text-xs font-semibold hover:underline">Ver todos</button>
          </div>
          <div className="divide-y divide-slate-50">
            {projects.length === 0 ? (
              <p className="text-slate-400 text-sm p-5">No tienes proyectos asignados.</p>
            ) : (
              projects.slice(0, 4).map((p) => (
                <div key={p.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}
                  data-testid={`project-lev-${p.id}`}>
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">{p.name}</p>
                    <p className="text-slate-400 text-xs">{p.status}</p>
                  </div>
                  <ArrowRight size={16} className="text-slate-300" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* My Events */}
        {events.length > 0 && (
          <div className="soem-card" id="my-events-card" data-testid="my-events-card">
            <div className="p-5 border-b border-slate-100 flex items-center gap-2">
              <Calendar size={16} className="text-soem-primary" />
              <h2 className="font-bold text-soem-navy">Mis Eventos Asignados</h2>
              <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">{events.length}</span>
            </div>
            <div className="divide-y divide-slate-50">
              {events.map(evt => {
                const evtCfg = EVENT_STATUS[evt.status] || EVENT_STATUS.planeacion;
                const riskCfg = RISK_LEVEL[evt.projected_risk_level] || RISK_LEVEL.medio;
                const dates = evt.dates || {};
                return (
                  <div key={evt.id} className="flex items-start justify-between px-5 py-4 hover:bg-blue-50/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/dashboard/events/${evt.id}`)} data-testid={`evt-lev-${evt.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-soem-navy text-sm truncate">{evt.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${evtCfg.bg} flex-shrink-0`}>{evtCfg.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-bold border ${riskCfg.bg} flex-shrink-0`}>{riskCfg.label}</span>
                      </div>
                      {dates.event_start && (
                        <p className="text-slate-400 text-xs">{new Date(dates.event_start).toLocaleDateString('es-MX')}</p>
                      )}
                      {evt.location && <p className="text-slate-400 text-xs">{evt.location.name}</p>}
                      <div className="flex gap-3 mt-1.5">
                        <EventTraceabilityStatus event={evt} />
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-slate-300 flex-shrink-0 mt-1" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
